export type PresentationPublication = 'local' | 'private' | 'published';

export interface PresentationDraft {
  id: string;
  title: string;
  subtitle: string;
  updatedAt: string;
  publication: PresentationPublication;
  ownerTenantId: string | null;
  source: 'local' | 'cloud';
  content: string;
}

export type SessionPhase =
  | 'idle'
  | 'loading'
  | 'ready'
  | 'dirty'
  | 'saving'
  | 'saved'
  | 'loadError'
  | 'saveError';

export interface PresentationSessionSnapshot {
  phase: SessionPhase;
  active: PresentationDraft | null;
  error: string | null;
  /** Non-error information for the user, e.g. that unsaved cloud edits were restored. */
  notice?: string | null;
  /**
   * Whether the unsaved edits of a cloud draft are mirrored on this device:
   * 'secured' (written), 'unavailable' (storage refused, e.g. quota/blocked), 'none' (nothing unsaved).
   */
  recovery?: 'secured' | 'unavailable' | 'none';
}

export interface PresentationRepository {
  list(): Promise<PresentationDraft[]>;
  create(input?: Partial<Pick<PresentationDraft, 'title' | 'subtitle'>>): Promise<PresentationDraft>;
  get(id: string): Promise<PresentationDraft | null>;
  save(draft: PresentationDraft): Promise<PresentationDraft>;
}

const LOCAL_DRAFTS_KEY = '3dp.local-drafts.v1';

export const DEFAULT_PRESENTATION = `---GLOBAL---
theme: arkturian-red
layouter: line
lineFixedSpacing: 3.5
lineJitter: 1.2
lineJitterSeed: 42
showLabel: true
labelRenderMode: CANVAS
labelDistance: 0.6
labelLatitude: -70
labelFontSize: 20
labelBillboardMode: true
labelMaxWidth: 520
cameraDistance: 4.0
mobileCameraDistance: 5.5
---

<!--
shape: sphere
color: #fff5ee
emissive: #ffffff
emissiveIntensity: 0.45
scale: 0.9
connectTo: 1
tubeRadius: 0.025
tubeColor: #fff5ee
-->
# Deine erste Idee

Beginne mit einer klaren Aussage. Jede Überschrift wird zu einer Station im Raum.

---

<!--
shape: sphere
color: #fff5ee
emissive: #ffffff
emissiveIntensity: 0.35
scale: 0.8
connectTo: 2
tubeRadius: 0.025
tubeColor: #fff5ee
-->
# Der Zusammenhang

Ordne Inhalte räumlich an und führe dein Publikum mit der Kamera hindurch.

---

<!--
shape: sphere
color: #fff5ee
emissive: #ffffff
emissiveIntensity: 0.5
scale: 1.0
tubeRadius: 0.025
tubeColor: #fff5ee
-->
# Der nächste Schritt

Ersetze diese Texte durch deine Geschichte — Bilder, Videos oder 3D-Modelle.
`;

function safeReadDrafts(): PresentationDraft[] {
  try {
    const raw = window.localStorage.getItem(LOCAL_DRAFTS_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed)
      ? parsed
          .filter((item) => item && typeof item.id === 'string')
          .map((item) => ({ ...item, content: typeof item.content === 'string' ? item.content : DEFAULT_PRESENTATION }))
      : [];
  } catch {
    return [];
  }
}

function safeWriteDrafts(drafts: PresentationDraft[]): void {
  window.localStorage.setItem(LOCAL_DRAFTS_KEY, JSON.stringify(drafts));
}

function createDraftId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return `local-${crypto.randomUUID()}`;
  }
  return `local-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

export class LocalDraftRepository implements PresentationRepository {
  async list(): Promise<PresentationDraft[]> {
    return safeReadDrafts().sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
  }

  async create(input: Partial<Pick<PresentationDraft, 'title' | 'subtitle'>> = {}): Promise<PresentationDraft> {
    const draft: PresentationDraft = {
      id: createDraftId(),
      title: input.title?.trim() || 'Unbenannte Präsentation',
      subtitle: input.subtitle?.trim() || 'Meine 3D-Story',
      updatedAt: new Date().toISOString(),
      publication: 'local',
      ownerTenantId: null,
      source: 'local',
      content: DEFAULT_PRESENTATION,
    };
    safeWriteDrafts([draft, ...safeReadDrafts()]);
    return draft;
  }

  async get(id: string): Promise<PresentationDraft | null> {
    return safeReadDrafts().find((draft) => draft.id === id) ?? null;
  }

  async save(draft: PresentationDraft): Promise<PresentationDraft> {
    const next = { ...draft, updatedAt: new Date().toISOString() };
    const drafts = safeReadDrafts();
    const index = drafts.findIndex((item) => item.id === next.id);
    if (index >= 0) drafts[index] = next;
    else drafts.unshift(next);
    safeWriteDrafts(drafts);
    return next;
  }
}

/**
 * Recovery copy for CLOUD drafts (#1758). A cloud draft is saved with PUT; while the
 * user edits, nothing is on disk. If the session expires (401) or the tab reloads,
 * the in-memory draft is gone. So every edit of a cloud draft is mirrored into
 * localStorage and restored on the next open, until a save succeeds.
 */
const CLOUD_RECOVERY_KEY = '3dp.cloud-recovery.v1';

interface RecoveryEntry {
  id: string;
  title: string;
  subtitle: string;
  content: string;
  savedAt: string;
}

function readRecoveryMap(): Record<string, RecoveryEntry> {
  try {
    const raw = window.localStorage.getItem(CLOUD_RECOVERY_KEY);
    const parsed = raw ? JSON.parse(raw) : {};
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
}

export function readRecovery(id: string): RecoveryEntry | null {
  return readRecoveryMap()[id] ?? null;
}

function writeRecovery(draft: PresentationDraft): boolean {
  try {
    const all = readRecoveryMap();
    all[draft.id] = { id: draft.id, title: draft.title, subtitle: draft.subtitle, content: draft.content, savedAt: new Date().toISOString() };
    window.localStorage.setItem(CLOUD_RECOVERY_KEY, JSON.stringify(all));
    return true;
  } catch {
    // Storage refused (quota, blocked): the caller must not claim the edits are secured.
    return false;
  }
}

export function clearRecovery(id: string): void {
  try {
    const all = readRecoveryMap();
    if (!(id in all)) return;
    delete all[id];
    window.localStorage.setItem(CLOUD_RECOVERY_KEY, JSON.stringify(all));
  } catch {
    // ignore
  }
}

type SessionListener = (snapshot: PresentationSessionSnapshot) => void;

export class PresentationSessionController {
  private readonly repository: PresentationRepository;
  private readonly listeners = new Set<SessionListener>();
  private snapshot: PresentationSessionSnapshot = { phase: 'idle', active: null, error: null };

  constructor(repository: PresentationRepository) {
    this.repository = repository;
  }

  getSnapshot = (): PresentationSessionSnapshot => this.snapshot;

  subscribe = (listener: SessionListener): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  async open(id: string | null): Promise<PresentationDraft> {
    this.setSnapshot({ phase: 'loading', active: null, error: null });
    try {
      const draft = id ? await this.repository.get(id) : await this.repository.create();
      if (!draft) throw new Error('Präsentation wurde nicht gefunden.');
      const recovery = draft.source === 'cloud' ? readRecovery(draft.id) : null;
      const differs = recovery && (recovery.content !== draft.content || recovery.title !== draft.title || recovery.subtitle !== draft.subtitle);
      if (recovery && differs) {
        // Unsaved edits from this device survive an expired session or a reload.
        const restored: PresentationDraft = { ...draft, title: recovery.title, subtitle: recovery.subtitle, content: recovery.content };
        const when = new Date(recovery.savedAt);
        const stamp = Number.isNaN(when.getTime()) ? '' : ` (Stand ${when.toLocaleString('de-AT')})`;
        this.setSnapshot({
          phase: 'dirty',
          active: restored,
          error: null,
          notice: `Nicht gespeicherte Änderungen von diesem Gerät wiederhergestellt${stamp}. Speichern übernimmt sie in die Cloud.`,
          recovery: 'secured',
        });
        return restored;
      }
      this.setSnapshot({ phase: 'ready', active: draft, error: null, recovery: 'none' });
      return draft;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Präsentation konnte nicht geladen werden.';
      this.setSnapshot({ phase: 'loadError', active: null, error: message });
      throw error;
    }
  }

  update(patch: Partial<Pick<PresentationDraft, 'title' | 'subtitle' | 'ownerTenantId' | 'content'>>): void {
    if (!this.snapshot.active) return;
    const changed = Object.entries(patch).some(([key, value]) => this.snapshot.active?.[key as keyof PresentationDraft] !== value);
    if (!changed) return;
    const active = { ...this.snapshot.active, ...patch };
    const recovery = active.source === 'cloud' ? (writeRecovery(active) ? 'secured' : 'unavailable') : 'none';
    this.setSnapshot({
      phase: 'dirty',
      active,
      error: null,
      notice: this.snapshot.notice ?? null,
      recovery,
    });
  }

  /**
   * Save into the draft's own repository: localStorage for `local-*` drafts, PUT to the
   * cloud for `cloud-*` drafts. Named `save`, not `saveLocal` (#1758): callers must not
   * mistake a cloud PUT for a local recovery snapshot. A successful save clears the
   * recovery copy of a cloud draft.
   */
  async save(): Promise<PresentationDraft> {
    if (!this.snapshot.active) throw new Error('Keine aktive Präsentation.');
    const submitted = this.snapshot.active;
    this.setSnapshot({ ...this.snapshot, phase: 'saving', error: null });
    try {
      const saved = await this.repository.save(submitted);
      // Edits that arrived while the request was in flight are NOT confirmed by this
      // save: keep them (memory and recovery copy) and stay dirty.
      const current = this.snapshot.active;
      const editedMeanwhile = current && current !== submitted
        && (current.content !== submitted.content || current.title !== submitted.title || current.subtitle !== submitted.subtitle);
      if (editedMeanwhile && current) {
        const merged: PresentationDraft = { ...saved, title: current.title, subtitle: current.subtitle, content: current.content };
        const recovery = merged.source === 'cloud' ? (writeRecovery(merged) ? 'secured' : 'unavailable') : 'none';
        this.setSnapshot({ phase: 'dirty', active: merged, error: null, notice: null, recovery });
        return saved;
      }
      clearRecovery(saved.id);
      this.setSnapshot({ phase: 'saved', active: saved, error: null, notice: null, recovery: 'none' });
      return saved;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Speichern fehlgeschlagen.';
      // The in-memory draft stays; for cloud drafts the recovery copy written by update() stays too.
      this.setSnapshot({ ...this.snapshot, phase: 'saveError', error: message });
      throw error;
    }
  }

  /**
   * Accept a repository-confirmed draft (e.g. after a publication change) without marking
   * it dirty again. Same revision rule as save() (#1758, Codex): the confirmation carries
   * the publication state, but it must not overwrite content/title/subtitle edited after
   * the confirmed revision was sent.
   */
  acceptSaved(draft: PresentationDraft): void {
    const current = this.snapshot.active;
    const editedMeanwhile = current && current.id === draft.id
      && (current.content !== draft.content || current.title !== draft.title || current.subtitle !== draft.subtitle);
    if (editedMeanwhile && current) {
      const merged: PresentationDraft = { ...draft, title: current.title, subtitle: current.subtitle, content: current.content };
      const recovery = merged.source === 'cloud' ? (writeRecovery(merged) ? 'secured' : 'unavailable') : 'none';
      this.setSnapshot({ phase: 'dirty', active: merged, error: null, notice: null, recovery });
      return;
    }
    clearRecovery(draft.id);
    this.setSnapshot({ phase: 'saved', active: draft, error: null, notice: null, recovery: 'none' });
  }

  private setSnapshot(snapshot: PresentationSessionSnapshot): void {
    this.snapshot = snapshot;
    this.listeners.forEach((listener) => listener(snapshot));
  }
}
